import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RefreshCw, RotateCcw } from "lucide-react";

import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { LoadScreen, PageBackButton } from "@/core/components/shared";
import { Button } from "@/core/components/ui/button";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useJournalEntry } from "@/features/ledger/api/use-journal-entry";
import {
  JournalEntryDetailCard,
  JournalEntryReverseDialog,
} from "@/features/ledger/components";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import { useSubmitApprovalRequest } from "@/features/shared/api/use-submit-approval-request";

/**
 * Détail d'une écriture comptable (`GET /JournalEntries/{id}`, guide §6.4) —
 * lignes débit/crédit et action de contre-passation (réservée à
 * flashpay-superadmin / flashpay-admin, écritures POSTED uniquement).
 * Contre-passation maker-checker (§5.4) : le dialog crée une demande
 * LEDGER_MANUAL_REVERSAL ; l'exécution réelle est faite par l'approbateur
 * depuis la file d'approbations.
 */
export function JournalEntryDetailPage() {
  const { t } = useLedgerTranslation();
  const navigate = useNavigate();
  const { journalEntryId = "" } = useParams();
  const { data: entry, isLoading, error, refetch } =
    useJournalEntry(journalEntryId);
  const submitApproval = useSubmitApprovalRequest();
  const [reverseOpen, setReverseOpen] = useState(false);
  const backButton = (
    <PageBackButton to="/app/ledger/journal" label={t("common.back")} />
  );

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex h-64 items-center justify-center">
          <LoadScreen />
        </div>
      </section>
    );
  }

  if (error || !entry) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="font-semibold text-destructive">
            {error?.message ?? t("error")}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("reload")}
          </Button>
        </div>
      </section>
    );
  }

  const canReverse = entry.status === "POSTED" && Boolean(entry.id);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {backButton}
          <h1 className="text-2xl font-semibold text-foreground">
            <NoTranslate as="span" className="font-mono text-xl">
              {entry.reference ?? journalEntryId}
            </NoTranslate>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t("reload")}
          </Button>
          {canReverse ? (
            <RequireRole
              roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
            >
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setReverseOpen(true)}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                {t("ledger.journal.actions.reverse")}
              </Button>
            </RequireRole>
          ) : null}
        </div>
      </div>

      <JournalEntryDetailCard entry={entry} />

      {canReverse && entry.id ? (
        <JournalEntryReverseDialog
          entry={{ ...entry, id: entry.id }}
          open={reverseOpen}
          onOpenChange={setReverseOpen}
          onConfirm={(reason) =>
            submitApproval.mutate(
              {
                actionType: "LEDGER_MANUAL_REVERSAL",
                sourceService: "ledger-service",
                resourceType: "JournalEntry",
                resourceId: entry.id as string,
                payload: {
                  reference: entry.reference,
                  sourceTransactionRef: entry.sourceTransactionRef,
                  productCode: entry.productCode,
                },
                reason,
              },
              {
                onSuccess: () => {
                  notifSilence(t("ledger.journal.feedback.approvalRequested"));
                  setReverseOpen(false);
                  navigate("/app/admin/approvals");
                },
                onError: (mutationError) =>
                  notifSilenceError(mutationError.message),
              }
            )
          }
          pending={submitApproval.isPending}
        />
      ) : null}
    </section>
  );
}
